import React, { useEffect, useState, useRef, useCallback } from "react"
import "./UserList.scss"
import AuthenticationApi from "Api/AuthenticationApi"
import * as Yup from "yup"
import { useNavigate } from "react-router-dom"
import {
  Button,
  Col,
  Modal,
  Row,
  Table,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
  Input,
} from "reactstrap"
import CustomBreadCrumb from "utils/CustomBreadCrumb/CustomBreadCrumb"
import { InputText } from "primereact/inputtext"
import { Dropdown } from "primereact/dropdown"
import DataTableLoader from "SkeletonLoader/DataTableLoader"
import { Toast } from "primereact/toast"
import { OverlayPanel } from "primereact/overlaypanel"
import { StandardPagination, StatusBadge, ActionIcon } from "components/Common"


//testing
const UserList = () => {
  document.title = "Users-List"
  const navigate = useNavigate()
  const accessToken = localStorage.getItem("accessToken")
  const userData = JSON.parse(localStorage.getItem("userData"))
  const permissions = userData?.permission || []
  const [page, setPage] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)
  const [limit] = useState(15)
  const [usersData, setUsersData] = useState([])
  const [loading, setLoading] = useState(true)
  const [addUserLoader, setAddUserLoader] = useState(false)
  const [filterStatus, setFilterStatus] = useState("all")
  const [searchTerm, setSearchTerm] = useState("")
  const [modal, setModal] = useState(false)
  const [errors, setErrors] = useState({})
  const op = useRef(null)
  const toast = useRef(null)

  const toggle = () => setModal(!modal)

  const breadcrumbItems = [
    { label: "Pixwik", link: "/" },
    { label: "Users", link: "/user-list" },
    { label: "All App Users", link: "#" },
  ]

  const handleBreadcrumbClick = link => {
    if (link !== "#") {
      navigate(link)
    }
  }

  const initialUserData = {
    userName: "",
    email: "",
    role: "",
  }

  const [addUserData, setAddUserData] = useState(initialUserData)

  const handleAddUserDataChange = (name, value) => {
    setAddUserData(prevData => ({
      ...prevData,
      [name]: value,
    }))
  }

  const validateAddUser = Yup.object().shape({
    userName: Yup.string().required("User name is required"),
    email: Yup.string()
      .email("Invalid email format")
      .required("Email is required"),
    role: Yup.string().oneOf(["sub admin", "admin"], "Invalid User Role"),
  })

  const getAllUsers = useCallback(() => {
    setLoading(true)
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      params: {
        limit: limit,
        page: page,
      },
    }

    AuthenticationApi.getAllUsers(config)
      .then(response => {
        setLoading(false)
        setUsersData(response.data.data.publicProfiles || [])
        setTotalRecords(response.data.totalUsers || 0)
      })
      .catch(error => {
        console.error("Error fetching Users:", error)
        setLoading(false)
      })
  }, [accessToken, limit, page])

  useEffect(() => {
    getAllUsers()
  }, [getAllUsers])

  const filteredUserDetails = usersData.filter(user => {
    // Filter by status
    const statusMatch = filterStatus === "all" || user.userType === filterStatus

    // Filter by search term
    const searchMatch = !searchTerm ||
      user.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.phoneNumber?.toLowerCase().includes(searchTerm.toLowerCase())

    return statusMatch && searchMatch
  })

  const totalPages = Math.ceil(totalRecords / limit)

  const capitalizeString = str => {
    return str
      .split(" ")
      .map(word => word.slice(0, 1).toLocaleUpperCase() + word.slice(1))
      .join(" ")
  }

  const roleOptions = [
    { name: "Sub Admin", value: "sub admin" },
    { name: "Admin", value: "admin" },
  ]

  const createUser = () => {
    setAddUserLoader(true)
    validateAddUser
      .validate(addUserData, { abortEarly: false })
      .then(() => {
        const config = {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
        AuthenticationApi.createUser(addUserData, config)
          .then(() => {
            getAllUsers()
            toggle()
            toast.current.show({
              severity: "success",
              summary: "Success",
              detail: "User Added successfully",
              life: 3000,
            })

            // toast.success("User Added successfully")
            setAddUserLoader(false)
          })
          .catch(error => {
            console.error("Error fetching user:", error)
            toast.current.show({
              severity: "error",
              summary: "Error",
              detail:
                error.response?.data?.message ||
                "Failed to create user. Please try again!",
              life: 3000,
            })
            // toast.error(
            //   error.response?.data?.message ||
            //     "Failed to create user. Please try again!",
            // )
            setAddUserLoader(false)
          })
      })
      .catch(err => {
        const validationErrors = {}
        err.inner.forEach(error => {
          validationErrors[error.path] = error.message
        })
        setErrors(validationErrors)
        // toast.error("Please fill all required fields correctly!")
        setAddUserLoader(false)
      })
  }



  const hasPermission = (permissions, resource, method) => {
    if (!Array.isArray(permissions.permission)) {
      console.error("Permissions is not an array:", permissions)
      return false
    }

    return permissions.permission.some(
      perm =>
        perm.permissionName === resource &&
        perm.permissionType.includes(method),
    )
  }

  const handleSendVerification = userId => {
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
    AuthenticationApi.UserVerification(userId, config)
      .then(() => {
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Verification sent successfully",
          life: 3000,
        })
        // toast.success("Verification sent successfully")
      })
      .catch(error => {
        console.error("Error sending verification:", error)
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to send verification",
          life: 3000,
        })
      })
  }

  return (
    <>
      <Toast ref={toast} position="top-right" life="3000" />
      <Row>
        <Col xs="12">
          <div className="user-list-header d-flex align-items-center justify-content-between">
            <CustomBreadCrumb
              data={breadcrumbItems}
              pageTitle="Users"
              handleClick={handleBreadcrumbClick}
            />

            <div className="d-flex align-items-center gap-3">
              <div className="search-container">
                <Input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
                <i className="mdi mdi-magnify search-icon"></i>
              </div>


            </div>
          </div>
          {(userData.role === "admin" ||
            hasPermission(permissions, "appUsers", "GET")) && (
              <div className="data-table-container">
                <div className="table-responsive mt-4 mb-3">
                  {loading ? (
                    <DataTableLoader cols={5} />
                  ) : (
                    <Table className="table mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>User Name</th>
                          <th>Email</th>
                          <th>Phone</th>
                          <th>
                            <span className="d-flex  align-items-center gap-2 justify-content-center">
                              <span>User Type</span>
                              <span
                                onClick={e => op.current.toggle(e)}
                                style={{ cursor: "pointer" }}
                              >
                                <i className="mdi mdi-filter" />
                              </span>
                            </span>
                            <OverlayPanel ref={op}>
                              <Input
                                type="select"
                                value={filterStatus}
                                onChange={e => {
                                  setFilterStatus(e.target.value)
                                  op.current.hide()
                                }}
                                style={{ width: "150px" }}
                              >
                                <option value="all">All</option>
                                <option value="student">Student</option>
                                <option value="creator">Creator</option>
                                <option value="employee">Employee</option>
                              </Input>
                            </OverlayPanel>
                          </th>
                          <th>Status</th>
                          {(userData.role === "admin" ||
                            hasPermission(permissions, "appUsers", "GET")) && (
                              <th>View</th>
                            )}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUserDetails?.map((user, index) => (
                          <tr key={user._id} className="user-table-row">
                            <th scope="row">{(page - 1) * limit + index + 1}</th>
                            <td>{user.userName}</td>
                            <td>{user.email}</td>
                            <td>{user.phoneNumber || "N/A"}</td>
                            <td className="text-center">
                              <span>
                                {capitalizeString(user.userType || "N/A")}
                              </span>
                            </td>

                            <td>
                              <StatusBadge
                                status={user.userType ? "active" : "inactive"}
                              />
                            </td>
                            {(userData.role === "admin" ||
                              hasPermission(permissions, "appUsers", "GET")) && (
                                <td>
                                  {user.isActive ? (
                                    <ActionIcon
                                      type="view"
                                      onClick={() => {
                                        if (user.userType) {
                                          navigate(`/view-user/${user.userId}`)
                                        }
                                      }}
                                      tooltip="View User"
                                      disabled={!user.userType}
                                    />
                                  ) : (
                                    <ActionIcon
                                      type="send-verification"
                                      onClick={() => handleSendVerification(user.userId)}
                                      tooltip="Send Verification"
                                    />
                                  )}
                                </td>
                              )}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </div>
              </div>
            )}

          <StandardPagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </Col>
      </Row>

      <div className="add-user-dialog">
        <Modal isOpen={modal} toggle={toggle} centered size="lg">
          <ModalHeader toggle={toggle}>Invite User</ModalHeader>
          <ModalBody style={{ maxHeight: '80vh', overflowY: 'auto', padding: '20px' }}>
            <div className="invite-user-form">
              <div className="form-group">
                <label htmlFor="userName"> Name</label>
                <InputText
                  value={addUserData.userName}
                  onChange={e => {
                    handleAddUserDataChange("userName", e.target.value)
                  }}
                  placeholder="Enter Name"
                />
                {errors.userName && (
                  <small className="error">{errors.userName}</small>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <InputText
                  value={addUserData.email}
                  onChange={e => {
                    handleAddUserDataChange("email", e.target.value)
                  }}
                  placeholder="Enter Email"
                />
                {errors.email && (
                  <small className="error">{errors.email}</small>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="userName">Role</label>
                <Dropdown
                  value={addUserData.role}
                  onChange={e => {
                    handleAddUserDataChange("role", e.value)
                  }}
                  optionLabel="name"
                  options={roleOptions}
                  placeholder="Choose Role"
                />
                {errors.role && <small className="error">{errors.role}</small>}
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="danger" onClick={toggle}>
              Cancel
            </Button>
            <Button
              color="secondary"
              onClick={() => {
                createUser()
              }}
            >
              {addUserLoader ? <Spinner size="sm" /> : " Add "}
            </Button>{" "}
          </ModalFooter>
        </Modal>
        {/* <ToastContainer /> */}
      </div>
    </>
  )
}

export default UserList
